import { useCallback, useRef, useState } from "react";

const useTaskRowExpansion = ({
	initialExpandedIds = [],
	initialPrimaryId = null,
	maxExpandedRows,
	taskCaption,
}) => {
	const [expandedIds, setExpandedIds] = useState(
		() => new Set(initialExpandedIds)
	);
	// The row the user is on, so a refresh restores position and not just which
	// rows were open.
	const [primaryId, setPrimaryId] = useState(initialPrimaryId);
	const [expansionMessage, setExpansionMessage] = useState("");
	const expandedIdsRef = useRef(expandedIds);

	const applyExpansion = useCallback((next) => {
		expandedIdsRef.current = next;
		setExpandedIds(next);
	}, []);

	const setExpanded = useCallback(
		(taskId, shouldExpand) => {
			const current = expandedIdsRef.current;
			if (
				shouldExpand &&
				!current.has(taskId) &&
				current.size >= maxExpandedRows
			) {
				setExpansionMessage(
					`Close an open ${taskCaption} before opening another (maximum ${maxExpandedRows}).`
				);
				return false;
			}
			setExpansionMessage("");
			const next = new Set(current);
			if (shouldExpand) next.add(taskId);
			else next.delete(taskId);
			applyExpansion(next);
			setPrimaryId(shouldExpand ? taskId : null);
			return true;
		},
		[applyExpansion, maxExpandedRows, taskCaption]
	);

	const expandWithoutLimit = useCallback(
		(taskId) => {
			setPrimaryId(taskId);
			if (expandedIdsRef.current.has(taskId)) return;
			applyExpansion(new Set(expandedIdsRef.current).add(taskId));
		},
		[applyExpansion]
	);

	return {
		expandedIds,
		primaryId,
		expansionMessage,
		setExpanded,
		expandWithoutLimit,
	};
};

export default useTaskRowExpansion;
